import React, { useEffect, useRef, useState } from "react";
import { DashedButton, Notification } from "@telia/teddy";
import { useSelector, useDispatch } from "react-redux";
import AddEquipmentModal from "./AddEquipmentModal";
import { FORM_ID } from "../../../../../constants";
import { RootState } from "../../../../../models";
import { isBroadbandCode, isEquipmentEligible } from "../../../../../shared/subscriptionCodeUtils";
import { PhoneNumberFormatters } from "../../../app/utils";
import { setActiveSubscription } from "../../../checkout/state/actions/cartActions";
import { getAllHardwareBundleItems } from "../../../checkout/state/selectors/cartSelectors";
import { getSubscriptions } from "../../../checkout/state/selectors/subscriptionsSelectors";
import { clearAll } from "../../../rapidform/state/actions";
import { Header } from "../../components/atoms/Header";
import { RetailerSubscriptionAccordion } from "../../components/organisms/RetailerSubscriptionAccordion";
import { changeExternalHardware } from "../../state/actions/hardwareActions";

const phoneNumberFormatter = PhoneNumberFormatters[0].displayFn;
const modemNumberFormatter = PhoneNumberFormatters[3].displayFn;

interface EquipmentNotification {
    variant: "gray" | "information";
    iconName?: "alert-filled";
    message: string;
}

const getNotificationConfig = (
    hasSubscriptions: boolean,
    hasEligibleSubscriptions: boolean,
): EquipmentNotification | null => {
    if (!hasSubscriptions) {
        return {
            variant: "gray",
            iconName: "alert-filled",
            message:
                "Ingen abonnement i handlekurven. Legg til et abonnement med telefonbinding først for å kunne velge utstyr.",
        };
    }
    if (!hasEligibleSubscriptions) {
        return {
            variant: "information",
            message:
                "Ingen abonnement i handlekurven som støtter utstyr. Merk at abonnement med tilbehørsbinding ikke trenger å registrere utstyr.",
        };
    }
    return null;
};

const Equipment = () => {
    const [showAddEquipmentModal, setShowAddEquipmentModal] = useState(false);
    const dispatch = useDispatch();
    const addEquipmentButtonRef = useRef<HTMLButtonElement>(null);

    const allHardwareBundles = useSelector((state: RootState) => getAllHardwareBundleItems(state));
    const subscriptions = useSelector((state: RootState) => getSubscriptions(state));
    const hasSubscriptions = Object.keys(subscriptions).length > 0;
    const hasEquipmentEligibleSubscriptions = Object.values(subscriptions).some((sub) => isEquipmentEligible(sub.code));
    const notificationConfig = getNotificationConfig(hasSubscriptions, hasEquipmentEligibleSubscriptions);

    useEffect(() => {
        if (hasEquipmentEligibleSubscriptions) {
            addEquipmentButtonRef.current?.focus();
        }
    }, [hasEquipmentEligibleSubscriptions]);

    return (
        <div className="retailer-dashboard-wrapper">
            {showAddEquipmentModal && (
                <AddEquipmentModal isModalOpen={showAddEquipmentModal} setIsModalOpen={setShowAddEquipmentModal} />
            )}
            <Header text="Utstyr" />
            <div className="retailer-main-section">
                {notificationConfig ? (
                    <Notification type="tile" variant={notificationConfig.variant}>
                        {notificationConfig.iconName ? (
                            <Notification.Icon name={notificationConfig.iconName} />
                        ) : (
                            <Notification.Icon />
                        )}
                        <Notification.Text>{notificationConfig.message}</Notification.Text>
                    </Notification>
                ) : (
                    <>
                        {allHardwareBundles.map((bundle) => {
                            const subscription = subscriptions[bundle.bundleId];
                            const subscriptionPhoneNumber = subscription?.phoneNumber?.selectedPhoneNumber;
                            const isMbbSubscription = isBroadbandCode(bundle.id);
                            const formattedNumber =
                                (isMbbSubscription
                                    ? modemNumberFormatter(subscriptionPhoneNumber || "")
                                    : phoneNumberFormatter(subscriptionPhoneNumber || "")
                                ).trim() || "Tlf.nr ikke valgt";
                            const productUser = subscription?.productUser?.firstName ?? "Bruker ikke valgt";
                            const subscriptionText = subscription
                                ? `${subscription.name} - ${formattedNumber} - ${productUser}`
                                : "";

                            return (
                                <RetailerSubscriptionAccordion
                                    id={bundle.bundleId}
                                    key={bundle.bundleId}
                                    title={subscriptionText}
                                    name={bundle.name}
                                    onSelectSubscription={() => {
                                        const { success } = dispatch(changeExternalHardware(bundle.bundleId));
                                        if (success) {
                                            setShowAddEquipmentModal(true);
                                        }
                                    }}
                                    isSelectable={false}
                                    isEditable
                                />
                            );
                        })}
                        <DashedButton
                            ref={addEquipmentButtonRef}
                            disabled={!hasEquipmentEligibleSubscriptions}
                            onClick={() => {
                                dispatch(clearAll(FORM_ID.EQUIPMENT_REGISTRATION));
                                dispatch(setActiveSubscription(""));
                                setShowAddEquipmentModal(true);
                            }}
                        >
                            Legg til utstyr
                        </DashedButton>
                    </>
                )}
            </div>
        </div>
    );
};

export default Equipment;
